import React, { useState } from 'react';
import { TableRow, TableCell, Typography, IconButton } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { ToggleInput } from '../../../../../../ui/input/toggle_input/toggle_input';
import { Permission } from '../../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../../utils/hooks/PermissionGate';
import usePermissions from '../../../../../../utils/hooks/usePermissions';
import { AssessmentUser } from '../../../../../../utils/types/assessment_types';

type AssessmentUserRowProps = {
  assessmentUser: AssessmentUser;
  deleteUserFromAssessment?: (assessmentUser: AssessmentUser) => void;
  changeUserActivityStatus?: (assessmentUser: AssessmentUser) => void;
};

export const AssessmentUserRow: React.FC<AssessmentUserRowProps> = ({
  assessmentUser,
  deleteUserFromAssessment,
  changeUserActivityStatus,
}) => {
  const { hasPermissions } = usePermissions();
  const [toggled, setToggled] = useState<boolean>(assessmentUser.enabled);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Completed':
        return 'success.main';
      case 'In Progress':
        return 'warning.main';
      case 'Scoring':
        return 'secondary.main';
      case 'Expired':
        return 'error.main';
      case 'Assigned':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
          {assessmentUser.userId}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
          {`${assessmentUser.firstName} ${assessmentUser.lastName}`}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
          {assessmentUser.email}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
          {assessmentUser.score}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: getStatusColor(assessmentUser.status),
            textTransform: 'capitalize',
          }}
        >
          {assessmentUser.status}
        </Typography>
      </TableCell>
      {changeUserActivityStatus && deleteUserFromAssessment && (
        <TableCell align="center">
          <ToggleInput
            toggled={toggled}
            handleToggle={() => {
              changeUserActivityStatus(assessmentUser);
              setToggled(!toggled);
            }}
            name="enabled"
            disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
          />
          <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
            <IconButton
              size="small"
              color="error"
              onClick={() => deleteUserFromAssessment(assessmentUser)}
              sx={{ ml: 1 }}
              aria-label="Delete user from assessment"
            >
              <Trash2 size={16} />
            </IconButton>
          </PermissionGate>
        </TableCell>
      )}
    </TableRow>
  );
};
